import React, { useState, useCallback } from 'react';
import { Heading, Text, Button, Flex } from '@vibe/core';
import { useReportStore } from '../../stores/reportStore';
import { createBookmark } from '../../api/report.api';
import type { PlannedVsActualReportResponse } from '../../api/report.api';
import PlannedVsActualExportButton from './PlannedVsActualExportButton';
import BookmarkDialog from './BookmarkDialog';
import { formatHours } from '../../utils/timesheet-capacity';

export type PlannedVsActualView = 'item' | 'board';

interface Props {
  data: PlannedVsActualReportResponse;
  activeView: PlannedVsActualView;
  onViewChange: (view: PlannedVsActualView) => void;
}

const PlannedVsActualHeader: React.FC<Props> = ({ data, activeView, onViewChange }) => {
  const { currentConfig, setView, setBookmarks, bookmarks } = useReportStore();
  const [showBookmarkDialog, setShowBookmarkDialog] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);

  const handleBookmark = useCallback(
    async (name: string) => {
      if (!currentConfig) return;
      setBookmarking(true);
      try {
        const bookmark = await createBookmark({
          name,
          reportType: 'planned_vs_actual',
          config: currentConfig,
        });
        setBookmarks([...bookmarks, bookmark]);
        setShowBookmarkDialog(false);
      } catch (err) {
        console.error('Failed to bookmark report:', err);
      } finally {
        setBookmarking(false);
      }
    },
    [currentConfig, bookmarks, setBookmarks],
  );

  const formatPeriodDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="rp-report-header">
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
      >
        <div>
          <Heading type={Heading.types.H2}>Planned vs Actual</Heading>
          <Text type={Text.types.TEXT2} style={{ color: '#676879', marginTop: 4 }}>
            Period: {formatPeriodDate(data.period.startDate)} &ndash;{' '}
            {formatPeriodDate(data.period.endDate)}
          </Text>
          <Flex gap={Flex.gaps.MEDIUM} style={{ marginTop: 4 }}>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.grandTotal.totalItems} items
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.grandTotal.originalEstimate === 0 ? '0' : formatHours(data.grandTotal.originalEstimate)}h estimated
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.grandTotal.timeSpent === 0 ? '0' : formatHours(data.grandTotal.timeSpent)}h spent
            </Text>
          </Flex>
        </div>
        <Flex gap={Flex.gaps.SMALL}>
          <PlannedVsActualExportButton data={data} activeView={activeView} />
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setShowBookmarkDialog(true)}
          >
            Bookmark
          </Button>
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('config')}
          >
            Customize Report
          </Button>
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('gallery')}
          >
            Back to Reports
          </Button>
        </Flex>
      </Flex>

      {/* View toggle */}
      <div className="rp-pva-view-toggle" style={{ marginTop: 16 }}>
        <button
          className={`rp-pva-view-toggle__btn${activeView === 'item' ? ' rp-pva-view-toggle__btn--active' : ''}`}
          onClick={() => onViewChange('item')}
        >
          By Item
        </button>
        <button
          className={`rp-pva-view-toggle__btn${activeView === 'board' ? ' rp-pva-view-toggle__btn--active' : ''}`}
          onClick={() => onViewChange('board')}
        >
          By Board
        </button>
      </div>

      {showBookmarkDialog && (
        <BookmarkDialog
          saving={bookmarking}
          onSave={handleBookmark}
          onClose={() => setShowBookmarkDialog(false)}
        />
      )}
    </div>
  );
};

export default PlannedVsActualHeader;